import { ChessGame } from './core/chessGame.ts';
import { ChessAI } from './ai/chessAI.ts';
import { SoundManager } from './audio/soundManager.ts';
import { SceneManager } from './graphics/sceneManager.ts';
import { MultiplayerClient } from './network/multiplayerClient.ts';
import {
  Square,
  PieceType,
  PieceColor,
  GameMode,
  DifficultyLevel,
  CameraViewId,
  MoveDetail
} from './core/types.ts';
import { BRANDING } from './config/branding.ts';

class WizardChessApp {
  // Core Systems
  private chessGame: ChessGame;
  private chessAI: ChessAI;
  private soundManager: SoundManager;
  private sceneManager: SceneManager;
  private multiplayer: MultiplayerClient;

  // Game Settings & State
  private gameMode: GameMode = 'vs-cpu';
  private playerColor: PieceColor = 'w';
  private cpuDifficulty: DifficultyLevel = 'medium';
  private selectedSquare: Square | null = null;
  private currentLegalMoves: { from: Square; to: Square; promotion?: PieceType; captured?: PieceType }[] = [];
  private pendingPromotion: { from: Square; to: Square } | null = null;
  private isProcessingMove = false;
  private autoRotateEnabled = false;

  constructor() {
    console.log(`[${BRANDING.gameTitle}] Initializing - ${BRANDING.developerName}`);

    this.chessGame = new ChessGame();
    this.chessAI = new ChessAI();
    this.soundManager = new SoundManager();

    const container = document.getElementById('canvas-container')!;
    this.sceneManager = new SceneManager(container, this.soundManager);

    this.multiplayer = new MultiplayerClient();
    this.setupMultiplayerCallbacks();

    this.initBoardPieces();
    this.setupEventListeners();
    this.setupModals();
    this.updateHUD();

    // Check URL parameters for direct room joining (e.g. ?room=123456)
    this.checkUrlForRoom();

    // Start castle ambience on first user interaction
    const startAudioOnce = () => {
      this.soundManager.startCastleAmbience();
      window.removeEventListener('click', startAudioOnce);
      window.removeEventListener('keydown', startAudioOnce);
      window.removeEventListener('touchstart', startAudioOnce);
    };
    window.addEventListener('click', startAudioOnce, { once: true });
    window.addEventListener('keydown', startAudioOnce, { once: true });
    window.addEventListener('touchstart', startAudioOnce, { once: true });
  }

  // --- Board Initialization ---
  private initBoardPieces(): void {
    const pieceView = this.sceneManager.getPieceView();
    pieceView.clearAll();

    const board = this.chessGame.getBoard();
    for (let r = 0; r < 8; r++) {
      for (let c = 0; c < 8; c++) {
        const item = board[r][c];
        if (item) {
          pieceView.createPiece(item.type, item.color, item.square);
        }
      }
    }

    this.sceneManager.getBoardView().hideAllHighlights();
  }

  // --- Interaction / Raycast Handling ---
  private onBoardClick(clientX: number, clientY: number): void {
    if (this.isProcessingMove || this.chessAI.calculating) return;

    // In online mode, can only move during our turn and our color
    if (this.gameMode === 'online') {
      const room = this.multiplayer.currentRoom;
      if (!room || room.status !== 'playing' || this.chessGame.turn !== room.myColor) {
        return;
      }
    }

    // In Vs CPU, can only select our own color
    if (this.gameMode === 'vs-cpu' && this.chessGame.turn !== this.playerColor) {
      return;
    }

    const clickedSquare = this.sceneManager.getSquareAtClientCoords(clientX, clientY);
    if (!clickedSquare) {
      this.deselect();
      return;
    }

    const piece = this.chessGame.getPiece(clickedSquare);

    // If already selected, check if clicked square is a legal destination
    if (this.selectedSquare) {
      const legalMatch = this.currentLegalMoves.find((m) => m.to === clickedSquare);
      if (legalMatch) {
        // Execute move
        if (this.chessGame.isPromotionMove(this.selectedSquare, clickedSquare)) {
          this.pendingPromotion = { from: this.selectedSquare, to: clickedSquare };
          this.openModal('modal-promotion');
        } else {
          this.executeMove(this.selectedSquare, clickedSquare, 'q');
        }
        return;
      }

      // If clicked on another piece of current player's color, switch selection
      if (piece && piece.color === this.chessGame.turn) {
        this.selectSquare(clickedSquare);
        return;
      }

      this.deselect();
      return;
    }

    // New selection: piece must belong to current turn
    if (piece && piece.color === this.chessGame.turn) {
      this.selectSquare(clickedSquare);
    }
  }

  private selectSquare(square: Square): void {
    this.selectedSquare = square;
    const boardView = this.sceneManager.getBoardView();
    boardView.setSelectedSquare(square);

    const moves = this.chessGame.getLegalMoves(square);
    this.currentLegalMoves = moves;

    const highlightData = moves.map((m) => ({
      to: m.to,
      isCapture: !!this.chessGame.getPiece(m.to) || (m.from[0] !== m.to[0] && this.chessGame.getPiece(m.from)?.type === 'p')
    }));

    boardView.showLegalMoves(highlightData);
    this.soundManager.playButtonClick();
  }

  private deselect(): void {
    this.selectedSquare = null;
    this.currentLegalMoves = [];
    this.sceneManager.getBoardView().setSelectedSquare(null);
    this.sceneManager.getBoardView().clearLegalMoves();
  }

  // --- Move Execution ---
  private executeMove(from: Square, to: Square, promotion: PieceType = 'q'): void {
    this.isProcessingMove = true;
    this.deselect();

    const moveDetail = this.chessGame.makeMove(from, to, promotion);
    if (!moveDetail) {
      this.isProcessingMove = false;
      return;
    }

    // Send to online opponent if in online mode
    if (this.gameMode === 'online') {
      this.multiplayer.sendMove(from, to, promotion);
    }

    // Animate visual piece transition
    this.sceneManager.getAnimationManager().animateMove(moveDetail, () => {
      this.isProcessingMove = false;
      this.onMoveCompleted(moveDetail);
    });
  }

  private onMoveCompleted(move: MoveDetail): void {
    const boardView = this.sceneManager.getBoardView();
    boardView.setLastMove(move.from, move.to);

    // Update Check indicator
    if (move.isCheck) {
      const kingSquare = this.chessGame.getKingSquare(this.chessGame.turn);
      boardView.setCheckSquare(kingSquare);
    } else {
      boardView.setCheckSquare(null);
    }

    this.updateHUD();

    // Check game over
    if (this.chessGame.isGameOver) {
      this.handleGameOver();
      return;
    }

    // Auto-rotate board in Pass & Play if enabled
    if (this.gameMode === 'pass-and-play' && this.autoRotateEnabled) {
      this.sceneManager.getCameraController().rotate180();
    }

    // Trigger AI move if in Vs CPU mode
    if (this.gameMode === 'vs-cpu' && this.chessGame.turn !== this.playerColor) {
      this.triggerAIMove();
    }
  }

  // --- CPU AI Turn ---
  private async triggerAIMove(): Promise<void> {
    this.setTurnBannerText("The Grandmaster is contemplating...", true);

    try {
      const res = await this.chessAI.getBestMove(this.chessGame.fen, this.cpuDifficulty);
      if (!res) return;

      const moveDetail = this.chessGame.makeMove(res.from, res.to, res.promotion || 'q');
      if (moveDetail) {
        this.isProcessingMove = true;
        this.sceneManager.getAnimationManager().animateMove(moveDetail, () => {
          this.isProcessingMove = false;
          this.onMoveCompleted(moveDetail);
        });
      }
    } catch (err) {
      console.error('AI move calculation error:', err);
      this.showToast('AI calculation failed, retrying...');
      this.setTurnBannerText("Your Turn", false);
    }
  }

  // --- Game Over Handling ---
  private handleGameOver(): void {
    const titleEl = document.getElementById('game-over-title')!;
    const subtitleEl = document.getElementById('game-over-subtitle')!;
    const reasonEl = document.getElementById('game-over-reason')!;

    if (this.chessGame.isCheckmate) {
      const winner = this.chessGame.turn === 'w' ? 'Black' : 'White';
      titleEl.innerText = 'Checkmate!';
      subtitleEl.innerText = `${winner} Army Victorious!`;
      reasonEl.innerText = 'The King has fallen in glorious arcane combat.';
      this.soundManager.playVictory();
    } else if (this.chessGame.isStalemate) {
      titleEl.innerText = 'Stalemate';
      subtitleEl.innerText = 'A Mystical Draw';
      reasonEl.innerText = 'No legal moves remain, yet the King is not in check.';
    } else if (this.chessGame.isThreefoldRepetition) {
      titleEl.innerText = 'Draw by Repetition';
      subtitleEl.innerText = 'Equilibrium Reached';
      reasonEl.innerText = 'The exact position has repeated three times.';
    } else {
      titleEl.innerText = 'Game Drawn';
      subtitleEl.innerText = 'Peace in the Realm';
      reasonEl.innerText = 'Neither side can force victory.';
    }

    this.openModal('modal-game-over');
  }

  // --- HUD Updates ---
  private updateHUD(): void {
    // 1. Turn Banner
    const isCheck = this.chessGame.inCheck;
    const turnColor = this.chessGame.turn;
    const bannerEl = document.getElementById('turn-banner')!;
    const textEl = document.getElementById('turn-text')!;
    const iconEl = document.getElementById('turn-icon')!;

    bannerEl.classList.toggle('check-warning', isCheck);
    iconEl.innerHTML = turnColor === 'w' ? '&#9812;' : '&#9818;';

    if (isCheck) {
      textEl.innerText = `${turnColor === 'w' ? 'White' : 'Black'} King in Check!`;
    } else {
      textEl.innerText = `${turnColor === 'w' ? 'White' : 'Black'}'s Turn`;
    }

    // 2. Active player cards
    const cardWhite = document.getElementById('card-white')!;
    const cardBlack = document.getElementById('card-black')!;
    cardWhite.classList.toggle('active-turn', turnColor === 'w');
    cardBlack.classList.toggle('active-turn', turnColor === 'b');
    cardWhite.classList.toggle('in-check', isCheck && turnColor === 'w');
    cardBlack.classList.toggle('in-check', isCheck && turnColor === 'b');

    // 3. Captured pieces and material score
    const captured = this.chessGame.getCapturedPieces();
    this.renderCapturedShelf('captured-by-white', captured.b, 'w');
    this.renderCapturedShelf('captured-by-black', captured.w, 'b');

    const matWhiteEl = document.getElementById('material-white')!;
    const matBlackEl = document.getElementById('material-black')!;
    if (captured.scoreAdvantage > 0) {
      matWhiteEl.innerText = `+${captured.scoreAdvantage}`;
      matBlackEl.innerText = '';
    } else if (captured.scoreAdvantage < 0) {
      matWhiteEl.innerText = '';
      matBlackEl.innerText = `+${Math.abs(captured.scoreAdvantage)}`;
    } else {
      matWhiteEl.innerText = '';
      matBlackEl.innerText = '';
    }

    // 4. Move history scroll
    this.renderMoveHistory();
  }

  private setTurnBannerText(text: string, isThinking = false): void {
    const textEl = document.getElementById('turn-text')!;
    textEl.innerText = text;
    const iconEl = document.getElementById('turn-icon')!;
    iconEl.innerHTML = isThinking ? '&#10024;' : (this.chessGame.turn === 'w' ? '&#9812;' : '&#9818;');
  }

  private renderCapturedShelf(containerId: string, pieces: PieceType[], color: PieceColor): void {
    const container = document.getElementById(containerId)!;
    container.innerHTML = '';

    const symbols: Record<PieceType, { w: string; b: string }> = {
      p: { w: '&#9817;', b: '&#9823;' },
      n: { w: '&#9816;', b: '&#9822;' },
      b: { w: '&#9815;', b: '&#9821;' },
      r: { w: '&#9814;', b: '&#9820;' },
      q: { w: '&#9813;', b: '&#9819;' },
      k: { w: '&#9812;', b: '&#9818;' }
    };

    // Sort by value (P < N < B < R < Q)
    const order: Record<PieceType, number> = { p: 1, n: 2, b: 3, r: 4, q: 5, k: 6 };
    const sorted = [...pieces].sort((a, b) => order[a] - order[b]);

    sorted.forEach((p) => {
      const span = document.createElement('span');
      span.className = 'captured-piece-pill';
      span.innerHTML = symbols[p][color === 'w' ? 'b' : 'w'];
      container.appendChild(span);
    });
  }

  private renderMoveHistory(): void {
    const history = this.chessGame.getHistory();
    const container = document.getElementById('move-list-container')!;
    const countEl = document.getElementById('history-count')!;
    countEl.innerText = `${history.length} turns`;
    container.innerHTML = '';

    for (let i = 0; i < history.length; i += 2) {
      const moveNum = Math.floor(i / 2) + 1;
      const whiteMove = history[i];
      const blackMove = history[i + 1];

      const row = document.createElement('div');
      row.className = 'move-row';

      const numCol = document.createElement('span');
      numCol.className = 'move-num';
      numCol.innerText = `${moveNum}.`;

      const wCol = document.createElement('span');
      wCol.className = 'move-w';
      wCol.innerText = whiteMove ? whiteMove.san : '';

      const bCol = document.createElement('span');
      bCol.className = 'move-b';
      bCol.innerText = blackMove ? blackMove.san : '';

      row.appendChild(numCol);
      row.appendChild(wCol);
      row.appendChild(bCol);
      container.appendChild(row);
    }

    container.scrollTop = container.scrollHeight;
  }

  // --- Multiplayer Callbacks ---
  private setupMultiplayerCallbacks(): void {
    this.multiplayer.setCallbacks({
      onRoomCreated: (room) => {
        document.getElementById('display-room-pin')!.innerText = room.roomId;
        this.openModal('modal-online-wait');
      },

      onRoomJoined: (room, fen) => {
        this.closeAllModals();
        this.gameMode = 'online';
        this.chessGame.load(fen);
        this.initBoardPieces();
        this.updateModeBadge(`Online Room #${room.roomId} (${room.myColor === 'w' ? 'White' : 'Black'})`);
        this.sceneManager.getCameraController().setView(room.myColor === 'w' ? 'white' : 'black');
        this.showToast(`Joined Chamber #${room.roomId}!`);
        this.updateHUD();
      },

      onGameStart: () => {
        this.closeAllModals();
        this.showToast('Adversary connected! Match begins!');
        const room = this.multiplayer.currentRoom;
        if (room) {
          this.sceneManager.getCameraController().setView(room.myColor === 'w' ? 'white' : 'black');
        }
      },

      onMoveReceived: (move) => {
        // Execute opponent move
        const moveDetail = this.chessGame.makeMove(move.from, move.to, move.promotion || 'q');
        if (moveDetail) {
          this.isProcessingMove = true;
          this.sceneManager.getAnimationManager().animateMove(moveDetail, () => {
            this.isProcessingMove = false;
            this.onMoveCompleted(moveDetail);
          });
        }
      },

      onResign: (byColor) => {
        const titleEl = document.getElementById('game-over-title')!;
        const subtitleEl = document.getElementById('game-over-subtitle')!;
        const reasonEl = document.getElementById('game-over-reason')!;

        titleEl.innerText = 'Resignation';
        subtitleEl.innerText = `${byColor === 'w' ? 'White' : 'Black'} has conceded.`;
        reasonEl.innerText = 'Honor remains intact through forfeiture.';
        this.openModal('modal-game-over');
      },

      onOpponentStatus: (status) => {
        this.showToast(`Opponent is ${status}`);
      },

      onError: (msg) => {
        this.showToast(`Error: ${msg}`);
      }
    });
  }

  // --- Event Listeners ---
  private setupEventListeners(): void {
    const canvasMount = document.getElementById('canvas-container')!;

    // Tap/Click on board
    canvasMount.addEventListener('pointerdown', (e) => {
      this.onBoardClick(e.clientX, e.clientY);
    });

    // Camera Selector
    const camSelect = document.getElementById('camera-select') as HTMLSelectElement;
    camSelect.addEventListener('change', (e) => {
      const val = (e.target as HTMLSelectElement).value as CameraViewId;
      this.sceneManager.getCameraController().setView(val);
      this.soundManager.playButtonClick();
    });

    // Sound toggle
    const audioToggle = document.getElementById('btn-audio-toggle')!;
    audioToggle.addEventListener('click', () => {
      const muted = this.soundManager.toggleMute();
      audioToggle.style.opacity = muted ? '0.45' : '1.0';
      this.showToast(muted ? 'Audio Muted' : 'Audio Enabled');
    });

    // Settings
    document.getElementById('btn-settings')!.addEventListener('click', () => {
      this.openModal('modal-settings');
      this.soundManager.playButtonClick();
    });

    // How to Play / Credits
    document.getElementById('btn-help')!.addEventListener('click', () => {
      this.openModal('modal-help');
      this.soundManager.playButtonClick();
    });

    // Mode Select Button
    document.getElementById('btn-mode-select')!.addEventListener('click', () => {
      this.openModal('modal-mode');
      this.soundManager.playButtonClick();
    });

    // New Game / Restart
    document.getElementById('btn-new-game')!.addEventListener('click', () => {
      if (confirm('Start a new match? Current board will be reset.')) {
        this.restartGame();
      }
    });

    // Undo Move
    document.getElementById('btn-undo')!.addEventListener('click', () => {
      if (this.gameMode === 'online') {
        this.showToast('Cannot undo moves in online ranked chamber');
        return;
      }
      this.undoMove();
    });

    // Hint
    document.getElementById('btn-hint')!.addEventListener('click', async () => {
      if (this.isProcessingMove || this.chessAI.calculating) return;
      this.showToast('Consulting ancient chess grimoire...');
      try {
        const hint = await this.chessAI.getBestMove(this.chessGame.fen, 'hard');
        if (hint) {
          this.sceneManager.getBoardView().setLastMove(hint.from, hint.to);
          this.showToast(`Suggested move: ${hint.from.toUpperCase()} \u2192 ${hint.to.toUpperCase()}`);
        }
      } catch {
        this.showToast('The runes are cloudy. Try again later.');
      }
    });

    // Flip 180°
    document.getElementById('btn-flip')!.addEventListener('click', () => {
      this.sceneManager.getCameraController().rotate180();
      this.soundManager.playButtonClick();
    });

    // Resign
    document.getElementById('btn-resign')!.addEventListener('click', () => {
      if (confirm('Do you wish to resign this match?')) {
        if (this.gameMode === 'online') {
          this.multiplayer.resign();
        } else {
          this.handleGameOver();
        }
      }
    });

    // Rematch button
    document.getElementById('btn-rematch')!.addEventListener('click', () => {
      this.closeAllModals();
      this.restartGame();
    });

    // Return to Lobby
    document.getElementById('btn-return-lobby')!.addEventListener('click', () => {
      this.closeAllModals();
      this.openModal('modal-mode');
    });
  }

  // --- Modals Setup ---
  private setupModals(): void {
    // Close buttons
    document.querySelectorAll('.modal-close-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const targetId = btn.getAttribute('data-close');
        if (targetId) this.closeModal(targetId);
      });
    });

    // Mode Selection cards
    const modeCards = document.querySelectorAll('.mode-card-select');
    modeCards.forEach((card) => {
      card.addEventListener('click', () => {
        modeCards.forEach((c) => c.classList.remove('selected'));
        card.classList.add('selected');

        const mode = card.getAttribute('data-mode') as GameMode;
        const subCpu = document.getElementById('sub-options-cpu')!;
        const subOnline = document.getElementById('sub-options-online')!;

        subCpu.style.display = mode === 'vs-cpu' ? 'flex' : 'none';
        subOnline.style.display = mode === 'online' ? 'flex' : 'none';
        this.soundManager.playButtonClick();
      });
    });

    // Sub-options: Army Color
    document.querySelectorAll('.sub-color-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.sub-color-btn').forEach((b) => b.classList.remove('btn-gold'));
        btn.classList.add('btn-gold');
      });
    });

    // Sub-options: Difficulty
    document.querySelectorAll('.sub-diff-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.sub-diff-btn').forEach((b) => b.classList.remove('btn-gold'));
        btn.classList.add('btn-gold');
      });
    });

    // Start Game Confirm from Mode modal
    document.getElementById('btn-start-game-confirm')!.addEventListener('click', () => {
      const selected = document.querySelector('.mode-card-select.selected');
      const mode = (selected?.getAttribute('data-mode') as GameMode) || 'vs-cpu';

      if (mode === 'vs-cpu') {
        const activeColorBtn = document.querySelector('.sub-color-btn.btn-gold');
        const colorVal = activeColorBtn?.getAttribute('data-color') || 'w';
        this.playerColor = colorVal === 'random' ? (Math.random() > 0.5 ? 'w' : 'b') : (colorVal as PieceColor);

        const activeDiffBtn = document.querySelector('.sub-diff-btn.btn-gold');
        this.cpuDifficulty = (activeDiffBtn?.getAttribute('data-diff') as DifficultyLevel) || 'medium';

        this.gameMode = 'vs-cpu';
        this.closeAllModals();
        this.restartGame();

        this.updateModeBadge(`Player vs CPU (${this.cpuDifficulty.toUpperCase()})`);
        this.sceneManager.getCameraController().setView(this.playerColor === 'w' ? 'white' : 'black');

        if (this.playerColor === 'b') {
          this.triggerAIMove();
        }
      } else if (mode === 'pass-and-play') {
        this.gameMode = 'pass-and-play';
        this.closeAllModals();
        this.restartGame();
        this.updateModeBadge('Local Pass & Play');
        this.sceneManager.getCameraController().setView('white');
      }
    });

    // Online: Create Room
    document.getElementById('btn-create-room')!.addEventListener('click', () => {
      this.gameMode = 'online';
      this.multiplayer.createRoom('random');
      this.updateModeBadge('Online (Waiting...)');
    });

    // Online: Join Room toggle
    document.getElementById('btn-join-room-tab')!.addEventListener('click', () => {
      document.getElementById('join-room-input-container')!.style.display = 'flex';
    });

    // Online: Confirm PIN
    document.getElementById('btn-confirm-join-pin')!.addEventListener('click', () => {
      const pin = (document.getElementById('join-pin-input') as HTMLInputElement).value;
      if (pin.trim().length === 6) {
        this.multiplayer.joinRoom(pin);
      } else {
        this.showToast('Please enter a 6-digit Chamber PIN');
      }
    });

    // Copy PIN button
    document.getElementById('btn-copy-pin')!.addEventListener('click', () => {
      const pin = document.getElementById('display-room-pin')!.innerText;
      navigator.clipboard.writeText(pin);
      this.showToast(`Chamber PIN ${pin} copied!`);
    });

    // Copy Link button
    document.getElementById('btn-copy-link')!.addEventListener('click', () => {
      const pin = document.getElementById('display-room-pin')!.innerText;
      const url = `${window.location.origin}${window.location.pathname}?room=${pin}`;
      navigator.clipboard.writeText(url);
      this.showToast('Chamber invite link copied!');
    });

    // Pawn Promotion selection
    document.querySelectorAll('.promotion-card').forEach((card) => {
      card.addEventListener('click', () => {
        const promoType = card.getAttribute('data-promo') as PieceType;
        if (this.pendingPromotion) {
          const { from, to } = this.pendingPromotion;
          this.pendingPromotion = null;
          this.closeModal('modal-promotion');
          this.executeMove(from, to, promoType);
        }
      });
    });

    // Volume Sliders
    (document.getElementById('slider-master') as HTMLInputElement).addEventListener('input', (e) => {
      const val = parseInt((e.target as HTMLInputElement).value, 10) / 100;
      this.soundManager.setMasterVolume(val);
    });

    (document.getElementById('slider-sfx') as HTMLInputElement).addEventListener('input', (e) => {
      const val = parseInt((e.target as HTMLInputElement).value, 10) / 100;
      this.soundManager.setSfxVolume(val);
    });

    (document.getElementById('slider-ambience') as HTMLInputElement).addEventListener('input', (e) => {
      const val = parseInt((e.target as HTMLInputElement).value, 10) / 100;
      this.soundManager.setAmbienceVolume(val);
    });

    // Graphics Quality buttons
    document.querySelectorAll('.setting-qual-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.setting-qual-btn').forEach((b) => b.classList.remove('btn-gold'));
        btn.classList.add('btn-gold');
        const qual = btn.getAttribute('data-qual') as 'high' | 'medium' | 'low';
        this.sceneManager.setQuality(qual);
        this.showToast(`Graphics set to ${qual.toUpperCase()}`);
      });
    });

    // Animation Speed buttons
    document.querySelectorAll('.setting-speed-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        document.querySelectorAll('.setting-speed-btn').forEach((b) => b.classList.remove('btn-gold'));
        btn.classList.add('btn-gold');
        const spd = btn.getAttribute('data-speed') as 'normal' | 'fast' | 'instant';
        this.sceneManager.getAnimationManager().animationSpeed = spd;
        this.showToast(`Combat speed set to ${spd.toUpperCase()}`);
      });
    });

    // Cinematic Kills Checkbox
    (document.getElementById('check-cinematic-kills') as HTMLInputElement).addEventListener('change', (e) => {
      this.sceneManager.getAnimationManager().cinematicKills = (e.target as HTMLInputElement).checked;
    });

    // Auto-rotate Checkbox
    (document.getElementById('check-autorotate') as HTMLInputElement).addEventListener('change', (e) => {
      this.autoRotateEnabled = (e.target as HTMLInputElement).checked;
    });
  }

  // --- Reset & Undo ---
  private restartGame(): void {
    this.chessGame.reset();
    this.initBoardPieces();
    this.updateHUD();
    this.deselect();
    this.showToast('The enchanted chessboard has been reset.');
  }

  private undoMove(): void {
    if (this.gameMode === 'vs-cpu') {
      // Undo both AI move and Player move
      this.chessGame.undoMove();
      this.chessGame.undoMove();
    } else {
      this.chessGame.undoMove();
    }

    this.initBoardPieces();
    this.updateHUD();
    this.deselect();
    this.showToast('Move rewound in the sands of time.');
  }

  private checkUrlForRoom(): void {
    const params = new URLSearchParams(window.location.search);
    const roomParam = params.get('room');
    if (roomParam && roomParam.length === 6) {
      this.showToast(`Found Chamber #${roomParam} in URL, joining...`);
      this.multiplayer.joinRoom(roomParam);
    }
  }

  private updateModeBadge(label: string): void {
    document.getElementById('mode-text-label')!.innerText = label;
  }

  // --- Modal Helpers ---
  private openModal(id: string): void {
    document.getElementById(id)?.classList.add('open');
  }

  private closeModal(id: string): void {
    document.getElementById(id)?.classList.remove('open');
  }

  private closeAllModals(): void {
    document.querySelectorAll('.modal-backdrop').forEach((m) => m.classList.remove('open'));
  }

  private showToast(message: string): void {
    const container = document.getElementById('toast-container')!;
    const toast = document.createElement('div');
    toast.className = 'toast';
    toast.innerText = message;
    container.appendChild(toast);

    setTimeout(() => toast.classList.add('show'), 10);
    setTimeout(() => {
      toast.classList.remove('show');
      setTimeout(() => container.removeChild(toast), 300);
    }, 2800);
  }
}

// Start application when DOM is ready
window.addEventListener('DOMContentLoaded', () => {
  new WizardChessApp();
});
